/**
 * HeroDemoVideo — 15-second looping screen capture of a real Lumora Studio
 * session (paste product link → import → generate product ad). Falls back to
 * the animated montage when the clip can't load or the user prefers reduced motion.
 */
import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play } from "lucide-react";
import { HeroDemoMontage } from "@/components/layout/HeroDemoMontage";

const SRC = "/demo/hero-demo.mp4";
const SRC_WEBM = "/demo/hero-demo.webm";
const POSTER = "/demo/hero-demo-poster.jpg";

export function HeroDemoVideo() {
  const ref = useRef(null);
  const [failed, setFailed] = useState(false);
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const onChange = (e) => setReduced(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    const v = ref.current;
    if (!v || reduced) return;
    const p = v.play();
    if (p && p.catch) p.catch(() => {});
  }, [reduced, failed]);

  if (failed) return <HeroDemoMontage />;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}
      className="relative w-full rounded-3xl overflow-hidden bg-[#07031a] ring-1 ring-white/10 shadow-2xl shadow-violet-900/30"
      data-testid="hero-demo-video"
    >
      <div className="flex items-center gap-2 px-4 h-9 bg-white/[0.04] border-b border-white/[0.06]">
        <span className="w-2.5 h-2.5 rounded-full bg-rose-400/60" />
        <span className="w-2.5 h-2.5 rounded-full bg-amber-300/60" />
        <span className="w-2.5 h-2.5 rounded-full bg-violet-400/60" />
        <span className="ml-3 flex-1 truncate rounded-md bg-black/30 px-3 py-1 text-[10px] font-mono text-white/40">lumoraai.com.au/studio</span>
        <span className="hidden sm:inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-violet-300"><span className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-pulse" />Real session</span>
      </div>
      <div className="relative aspect-[16/10] bg-black">
        <video
          ref={ref}
          className="absolute inset-0 w-full h-full object-cover"
          poster={POSTER}
          muted
          loop
          playsInline
          autoPlay={!reduced}
          preload="metadata"
          aria-label="Screen recording: a product link is pasted into Lumora Studio, the product is imported and a finished ad is generated"
          data-testid="hero-demo-video-el"
        >
          <source src={SRC} type="video/mp4" />
          <source src={SRC_WEBM} type="video/webm" onError={() => setFailed(true)} />
        </video>
        {reduced && (
          <button type="button" onClick={() => { setReduced(false); }} data-testid="hero-demo-video-play"
            className="absolute inset-0 flex items-center justify-center bg-black/30">
            <span className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center shadow-xl"><Play className="w-6 h-6 text-violet-700 fill-violet-700 ml-0.5" /></span>
          </button>
        )}
        <div className="absolute bottom-3 right-3 pointer-events-none">
          <span className="rounded-full bg-black/55 backdrop-blur-sm px-3 py-1.5 text-[10px] font-semibold text-white/85 ring-1 ring-white/10">
            Link → import → finished ad · 15s, sped up only
          </span>
        </div>
      </div>
    </motion.div>
  );
}
